<template>
  <el-dialog
    :model-value="isOpen"
    width="744px"
    fullscreen
    class="is-side"
    :before-close="confirmAndClose"
  >
    <div v-if="isOpen">
      <div class="tw-mx-8 tw-mt-3 tw-relative">
        <div class="tw-flex tw-items-center tw-mb-1">
          <icon-star
            v-if="permit.is_main"
            class="tw-w-4 tw-h-4 tw-text-primary dark:tw-text-dm-primary tw-mr-1"
          />
          <span class="tw-text-xs tw-leading-[14px] tw-text-gray-dark dark:tw-text-dm-gray-dark">
            {{ permit.reference_number }}
          </span>
        </div>
        <h2 class="tw-tracking-wide tw-leading-[28px]">
          {{ permit.permit_type_name }}
        </h2>
      </div>

      <div
        v-if="showPermitObtainedForm || showEditObtainedPermitForm"
        class="tw-p-10 tw-pb-28"
      >
        <h3
          class="tw-mb-4 tw-text-center"
        >
          {{ $t( showPermitObtainedForm ? 'foreigners.permits.permit_obtain_dialog.title' : 'foreigners.permits.permit_edit_obtain_dialog.title') }}
        </h3>

        <el-form
          ref="ObtainPermitForm"
          class="tw-flex tw-flex-col"
          :model="form"
          :rules="formRules"
        >
          <div class="tw-flex tw-gap-2 tw-mb-2">
            <el-form-item
              :label="$t('foreigners.permits.permit_obtain_dialog.issue_date')"
              prop="issue_date"
            >
              <el-date-picker
                v-model="form.issue_date"
                type="date"
                :placeholder="$t('common.fields.select_date')"
                format="DD.MM.YYYY"
                value-format="DD.MM.YYYY"
                style="width: 100%"
              />
            </el-form-item>
            <el-form-item
              :label="$t('foreigners.permits.permit_obtain_dialog.expiry_date')"
              prop="expire_date"
            >
              <el-date-picker
                v-model="form.expire_date"
                type="date"
                :placeholder="$t('common.fields.select_date')"
                format="DD.MM.YYYY"
                value-format="DD.MM.YYYY"
                style="width: 100%"
              />
            </el-form-item>
          </div>
          <i-upload
            :value="fileUploadInputValue"
            multiple
            :max-filename-label-length="73"
            @changed="filesChanged"
          />
        </el-form>
      </div>

      <div
        v-else
        class="tw-border-b tw-border-gray dark:tw-border-dm-gray tw-px-8"
      >
        <el-tabs
          :model-value="tab"
          @tab-click="changeTab"
        >
          <el-tab-pane
            name="status"
            :label="$t('foreigners.permits.statuses')"
          />
          <el-tab-pane
            name="documents"
          >
            <template #label>
              <span class="tw-flex tw-items-center">
                {{ $t('foreigners.permits.documents') }}
                <i-badge
                  class="tw-ml-2"
                  type="primary"
                  size="small"
                  is-circle
                >
                  {{ permitDocuments.filter((permitDocument) => permitDocument.status === $store.state.v2.admin.foreigners.foreigner.PERMIT_STATUS_MISSING_ID).length }}
                </i-badge>
              </span>
            </template>
          </el-tab-pane>
        </el-tabs>
      </div>

      <div v-loading="isLoading">
        <div
          v-if="tab === 'status' && !showPermitObtainedForm && !showEditObtainedPermitForm"
          class="tw-px-8 tw-py-6 tw-pb-20"
        >
          <IStepProgressWithLabels
            v-if="permitStatuses.length > 0"
            :statuses="permitStatuses"
            :active-status="activeStatus"
            :is-obtained="isObtained"
            :on-status-click="onStatusClick"
            class="tw-mb-6"
          />
          <PermitStatus
            v-if="activeStatus"
            :status="activeStatus"
            :current-status="currentStatus"
            :permit="permit"
            :is-obtained="isObtained"
            class="tw-mb-3"
            @change-current-status="changeCurrentStatus"
          />
        </div>
        <div
          v-if="tab === 'documents' && !showPermitObtainedForm && !showEditObtainedPermitForm"
          class="tw-px-8 tw-py-6 tw-pb-24"
        >
          <PermitDocument
            v-for="permitDocument in permitDocuments.filter((document) => !document.delete_document)"
            :key="permitDocument.id"
            :permit="permit"
            :permit-document="permitDocument"
            :is-obtained="isObtained"
            class="tw-mb-3"
          />

          <PermitDocumentAdd :permit="permit" />
        </div>

        <div
          class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-gap-2 tw-border-t tw-border-gray dark:tw-border-dm-gray tw-py-3 tw-px-8 tw-fixed tw-z-20 tw-bottom-0 tw-right-0 tw-w-full tw-bg-white dark:tw-bg-dm md:tw-max-w-3xl lg:tw-max-w-4xl xl:tw-max-w-6xl 2xl:tw-max-w-7xl"
        >
          <div
            v-if="$isInRole(['ADMIN', 'CONSULTANT'])"
            class="tw-flex tw-gap-2"
          >
            <i-button
              v-if="!permit.is_main"
              size="x-medium"
              @click="setPermitAsMain"
            >
              <icon-star class="tw-text-primary dark:tw-text-dm-primary" />
              <span>
                {{ $t('foreigners.permits.set_main_short') }}
              </span>
            </i-button>
            <i-button
              v-if="isObtained"
              size="x-medium"
              class="tw-opacity-100"
              disabled
            >
              <icon-check />
              <span>
                {{ $t('foreigners.permits.permit_obtained') }}
              </span>
            </i-button>
            <i-button
              v-else
              size="x-medium"
              @click="showPermitObtainedForm = true"
            >
              <span>
                {{ $t('foreigners.permits.mark_obtained') }}
              </span>
            </i-button>
            <i-button
              v-if="isObtained"
              size="x-medium"
              is-icon
              @click="showEditObtainedPermitForm = true"
            >
              <icon-edit />
            </i-button>

            <i-confirm-popover
              :cancel-text="$t('common.actions.cancel')"
              :submit-text="$t('common.actions.delete')"
              @submit="deletePermit"
            >
              <template #reference>
                <i-button
                  size="x-medium"
                  is-icon
                  :is-loading="isDeleting"
                >
                  <icon-trash class="tw-text-primary dark:tw-text-dm-primary" />
                </i-button>
              </template>
              <template #content>
                <div>
                  {{ $t('foreigners.permits.delete_permit_dialog.confirm_text') }}
                </div>
              </template>
            </i-confirm-popover>
          </div>
          <div class="tw-flex tw-justify-start tw-gap-2">
            <i-button
              class="tw-px-10"
              @click="confirmAndClose()"
            >
              {{ $t('common.actions.cancel') }}
            </i-button>
            <i-button
              type="primary"
              class="tw-px-10"
              @click="save"
            >
              {{ $t('common.actions.save') }}
            </i-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import PermitDocument from '@/components/Permits/PermitDocument.vue';
import PermitDocumentAdd from '@/components/Permits/PermitDocumentAdd.vue';
import PermitStatus from '@/components/Permits/PermitStatus.vue';
import { deepDiff, getValidationError } from '@/helpers';
import IStepProgressWithLabels from '@/components/ui/i-step-progress-with-labels.vue';
import { mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import eventBus from '../../plugins/eventBus';

export default {
  components: {
    IStepProgressWithLabels,
    PermitDocument,
    PermitDocumentAdd,
    PermitStatus,
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    permit: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      isDeleting: false,
      tab: 'status',
      showPermitObtainedForm: false,
      showEditObtainedPermitForm: false,
      form: {
        issue_date: '',
        expire_date: '',
        documents: [],
      },
      activeStatus: null,
      formRules: {
        issue_date: [
          { required: true, message: 'Required' },
          {
            validator: (rule, value, callback) => {
              if (this.form.expire_date && this.parseDate(value) > this.parseDate(this.form.expire_date)) {
                callback(new Error('Issue date must be before expiry date'));
              } else {
                callback();
              }
            },
            trigger: 'change',
          },
        ],
        expire_date: [
          { required: true, message: 'Required' },
          {
            validator: (rule, value, callback) => {
              if (this.form.issue_date && this.parseDate(this.form.issue_date) > this.parseDate(value)) {
                callback(new Error('Issue date must be before expiry date'));
              } else {
                callback();
              }
            },
            trigger: 'change',
          },
        ],
      },
    };
  },

  computed: {
    ...mapState({
      foreigner: (state) => state.v2.admin.foreigners.foreigner.foreigner,
      initialPermitDocuments: (state) => state.v2.admin.foreigners.foreigner.permitDocuments,
      initialPermitStatuses: (state) => state.v2.admin.foreigners.foreigner.permitStatuses,
      permitDocuments: (state) => state.v2.admin.foreigners.foreigner.permitDocumentsUpdateDraft,
      permitStatuses: (state) => state.v2.admin.foreigners.foreigner.permitStatusesUpdateDraft,
    }),
    ...mapGetters({
      internalUsersAreEmpty: 'v2/admin/cms/internal/users/selectOptionsAreEmpty',
    }),
    currentStatus() {
      return this.permitStatuses.find(({ is_current }) => Boolean(is_current));
    },
    isObtained() {
      return this.permit.permit_status === 30;
    },
    initialTab() {
      return this.$isInRole(['ADMIN', 'CONSULTANT']) ? 'documents' : 'status';
    },
    canClickOnFutureStatus() {
      return this.$isInRole(['ADMIN', 'CONSULTANT']);
    },
    fileUploadInputValue() {
      if (this.form.documents?.length > 1) return `${this.$tc('common.field.files_uploaded_label', this.form.documents.length )}`;
      return this.form.documents?.[0]?.name;
    },
  },
  watch: {
    isOpen(isOpenValue) {
      if (isOpenValue) {
        this.loadPermitData();
        this.changeTab({ props: { name: this.initialTab } });
      }
    },
    currentStatus() {
      if (this.currentStatus) {
        this.activeStatus = cloneDeep(this.currentStatus);
      }
    },
  },
  created() {
    if (this.isOpen) {
      this.loadPermitData();
    }
  },

  mounted() {
    // Match the pattern in the URL
    const match = this.$route.path.match(/foreigners\/[^/]+\/permits\/[^/]+\/(\w+)/i);

    if (match) {
      const tabFromURL = match[1];

      if (['status', 'documents'].includes(tabFromURL)) {
        this.tab = tabFromURL;
      }
    } else {
      this.changeTab({
        props: {
          name: this.initialTab,
        },
      });
    }
  },

  methods: {
    parseDate(dateString) {
      const [day, month, year] = dateString.split('.').map(Number);
      return new Date(year, month - 1, day); // month is zero-based in JavaScript Date
    },
    changeTab(tab) {
      const name = tab.props.name;
      this.tab = name;
      const baseUrl = window.location.origin;
      const foreignerId = window.location.pathname.split('/')[2];
      const permitId = window.location.pathname.split('/')[4];

      console.log('foreignerId', foreignerId);
      console.log('permitId', permitId);
      console.log('name', name);

      if (foreignerId && permitId && this.isOpen) {
        let newUrl = `${baseUrl}/foreigners/${foreignerId}/permits/${permitId}`;

        if (name) {
          newUrl += `/${name}`;
        }

        window.history.pushState({}, '', newUrl);
      }
    },
    async filesChanged(payload) {
      const files = await Promise.all(payload.map(async(source) => {
        const binaryFile = new Blob([source], { type: source.type });
        return {
          file: binaryFile,
          name: source.name,
        };
      }));

      this.form = {
        ...this.form,
        documents: files,
      };
    },
    loadPermitData() {
      this.$store.dispatch('v2/admin/foreigners/foreigner/loadPermitDocuments', {
        foreignerId: this.foreigner.id,
        permitId: this.permit.id,
      });

      this.$store.dispatch('v2/admin/foreigners/foreigner/loadPermitStatuses', {
        foreignerId: this.foreigner.id,
        permitId: this.permit.id,
      });

      this.$store.dispatch('v2/admin/cms/internal/places/loadAllPLaces');

      if (this.internalUsersAreEmpty) {
        this.$store.dispatch('v2/admin/cms/internal/users/loadAllUsers');
      }
    },
    onStatusClick(status) {
      if (this.$isInRole(['ADMIN', 'CONSULTANT'])) {
        if (status.id !== this.activeStatus?.id) {
          this.$store.commit('v2/admin/foreigners/foreigner/update', ['permitStatusesUpdateDraft', {
            id: this.activeStatus?.id,
            payload: {
              ...this.activeStatus,
              is_current: false,
            },
          }]);
        }

        this.$store.commit('v2/admin/foreigners/foreigner/update', ['permitStatusesUpdateDraft', {
          id: status.id,
          payload: {
            ...status,
            isUpdated: true,
            is_current: true,
          },
        }]);
        this.activeStatus = { ...status, is_current: true };
      }
    },
    changeCurrentStatus(status, isCurrent) {
      if (status.id !== this.currentStatus?.id) {
        this.$store.commit('v2/admin/foreigners/foreigner/update', ['permitStatusesUpdateDraft', {
          id: this.currentStatus?.id,
          payload: {
            ...this.currentStatus,
            is_current: false,
          },
        }]);
      }

      this.$store.commit('v2/admin/foreigners/foreigner/update', ['permitStatusesUpdateDraft', {
        id: status.id,
        payload: {
          ...status,
          isUpdated: true,
          is_current: isCurrent,
        },
      }]);
      this.activeStatus = { ...status, is_current: isCurrent };
    },
    async setPermitAsMain() {
      try {
        await this.$store.dispatch('v2/admin/foreigners/foreigner/editPermit', {
          permitId: this.permit.id,
          foreignerId: this.foreigner.id,
          payload: {
            is_main: true,
          },
        });

        await this.$store.dispatch('v2/admin/foreigners/foreigner/loadPermits', this.foreigner.id);
      } catch (error) {
        this.$message.error({
          message: this.$capitalize(this.$t('core.error.internal_server_error')),
          showClose: true,
        });
      }
    },
    async deletePermit() {
      this.isDeleting = true;
      await this.$store.dispatch('v2/admin/foreigners/foreigner/deletePermit', {
        foreignerId: this.foreigner.id,
        permitId: this.permit.id,
      });
      this.$emit('close');
      this.isDeleting = false;
    },

    async savePermitStatuses() {
      const statusesIdsToCollapse = [];
      await Promise.all(this.permitStatuses.map(async(permitStatus) => {
        if (permitStatus.isUpdated) {
          const initialPermitStatus = this.initialPermitStatuses.find(({ id }) => id === permitStatus.id);
          const payload = {
            ...deepDiff(initialPermitStatus, permitStatus),
            id: permitStatus.id,
            action_dates: permitStatus.action_dates.map((action_date) => ({
              id: action_date.id,
              place_id: action_date.place_id,
              action_date: action_date.action_date,
              action_time: action_date.action_time,
              assistance_id: action_date.assistance_id,
              note: action_date.note,
              share_with_foreigner: action_date.share_with_foreigner,
              share_with_client: action_date.share_with_foreigner,
            })),
          };
          if (!payload.is_current) {
            // TODO: temporary fix
            //  In case first request sending is_current as false, so backend can't accept it,
            //  because before the second request with is_current: true received,
            //  there is no permit status with is_current: true
            delete payload.is_current;
          }
          try {
            await this.$store.dispatch('v2/admin/foreigners/foreigner/editPermitStatus', {
              foreignerId: this.foreigner.id,
              permitId: this.permit.id,
              statusId: permitStatus.id,
              payload,
            });
            statusesIdsToCollapse.push(permitStatus.id);
          } catch (error) {
            const errorMessage = getValidationError(error);
            this.$message.error({
              message: errorMessage ?? this.$capitalize(this.$t('core.error.internal_server_error')),
              showClose: true,
              dangerouslyUseHTMLString: true,
            });
          }
        }
      }));
      return statusesIdsToCollapse;
    },

    async savePermitDocuments() {
      try {
        const documentIdsToCollapse = [];
        const documentsToUpdate = this.permitDocuments.filter((document) => document.isUpdated);
        const documentsWithOnlyChangedFields = documentsToUpdate.map((document) => {
          const initialDocument = this.initialPermitDocuments.find(({ id }) => id === document.id);
          const deepDiffObejct = deepDiff(initialDocument, document);
          return {
            ...deepDiffObejct,
            id: initialDocument.id,
            delete_file: document.delete_file ?? undefined,
            delete_document: document.delete_document ?? undefined,
            files: document.files ?? undefined,
          };
        });

        if (documentsWithOnlyChangedFields.length > 0 ) {
          await this.$store.dispatch('v2/admin/foreigners/foreigner/editPermitDocuments', {
            permitId: this.permit.id,
            foreignerId: this.$store.state.v2.admin.foreigners.foreigner.foreigner.id,
            payload: documentsWithOnlyChangedFields,
          });
        }
        documentsWithOnlyChangedFields.forEach((changedDocument) => {
          documentIdsToCollapse.push(changedDocument.id);
        });
        return documentIdsToCollapse;
      } catch (error) {
        const errorText = getValidationError(error);
        this.$message.error({
          message: this.$capitalize(errorText ?? this.$t('core.error.internal_server_error')),
          showClose: true,
          dangerouslyUseHTMLString: true,
        });
      }
    },

    async save() {
      this.isLoading = true;

      let statusesEditToCollapse;
      let documentsEditToCollapse;

      if (this.showPermitObtainedForm || this.showEditObtainedPermitForm) {
        const isValid = await this.$refs.ObtainPermitForm.validate().catch(() => false);
        if (!isValid) {
          this.isLoading = false;
          return;
        }
        try {
          if (this.showPermitObtainedForm) {
            await this.$store.dispatch('v2/admin/foreigners/foreigner/markPermitAsObtained', {
              permitId: this.permit.id,
              foreignerId: this.foreigner.id,
              payload: this.form,
            });
          } else if (this.showEditObtainedPermitForm) {
            const requests = [
              this.$store.dispatch('v2/admin/foreigners/foreigner/editObtainedPermit', {
                permitId: this.permit.id,
                foreignerId: this.foreigner.id,
                payload: this.form,
              }),
              this.$store.dispatch('v2/admin/foreigners/foreigner/editObtainedPermitDocument', {
                permitId: this.permit.id,
                foreignerId: this.foreigner.id,
                documents: this.form.documents,
                documentId: this.permit.final_document_id,
              }),
            ];

            await Promise.all(requests);
          }

          this.form = {
            issue_date: '',
            expire_date: '',
            documents: [],
          };
          this.showPermitObtainedForm = false;
          this.showEditObtainedPermitForm = false;
        } catch (e) {
          const errorText = getValidationError(e);

          this.$message.error({
            message: errorText,
            showClose: true,
            dangerouslyUseHTMLString: true,
          });
        }
      } else {
        statusesEditToCollapse = await this.savePermitStatuses();
        documentsEditToCollapse = await this.savePermitDocuments();
      }

      if ((statusesEditToCollapse?.length ?? 0) > 0) {
        statusesEditToCollapse.forEach((statusID) => {
          eventBus.emit('collapse-status', statusID);
        });
      }

      if ((documentsEditToCollapse?.length ?? 0) > 0) {
        documentsEditToCollapse.forEach((documentID) => {
          eventBus.emit('collapse-document', documentID);
        });
      }

      await this.$store.dispatch('v2/admin/foreigners/foreigner/loadPermits', this.foreigner.id);

      this.isLoading = false;
    },
    async confirmAndClose() {
      if (this.showPermitObtainedForm || this.showEditObtainedPermitForm) {
        this.showPermitObtainedForm = false;
        this.showEditObtainedPermitForm = false;
        this.form = {
          issue_date: '',
          expire_date: '',
          documents: [],
        };
        return;
      }
      const hasSomeStatusUpdated = this.permitStatuses.find(({ isUpdated }) => Boolean(isUpdated));
      const hasSomeDocumentUpdated = this.permitDocuments.find(({ isUpdated }) => Boolean(isUpdated));
      if (hasSomeStatusUpdated || hasSomeDocumentUpdated) {
        this.$confirm({
          title: this.$t('foreigners.permits.permit_obtain_dialog.confirm_close'),
          confirmButtonText: this.$t('common.actions.close'),
          cancelButtonText: this.$t('common.actions.cancel'),
        }).then(() => {
          this.$emit('close');
        }).catch(() => {});
      } else {
        this.$emit('close');
      }
    },
  },
};
</script>
